// hooks/useFollowRequestsList.ts — manage-list hook for Settings → Privacy →
// Follow requests, AND the inline Accept/Decline buttons on a follow_request
// notification (same accept/decline calls, so followStore/notification state
// stays correct without a reload either way). Mirrors useMutedAccountsList.ts.
//
// Components must NOT import followsService directly.

import { useCallback, useEffect, useRef, useState } from 'react';
import { followsService } from '@/core/services/followsService';
import { ApiError } from '@/core/lib/apiClient';
import type { SuggestedProfile } from '@/core/models/account';

/**
 * 'accepted' / 'declined' — the request was resolved; the row is removed.
 * 'stale'   — the request was already handled (e.g. from another tab, or the
 *             notification and the manage list both tried to resolve it) —
 *             the call 404s server-side; treat as already-gone, not an error.
 * 'error'   — the request itself failed.
 * 'busy'    — a request for this handle was already in flight (double-click guard).
 */
export type FollowRequestOutcome = 'accepted' | 'declined' | 'stale' | 'error' | 'busy';

export interface FollowRequestsListHook {
  requests: SuggestedProfile[];
  loading: boolean;
  error: boolean;
  pending: Set<string>;
  accept: (handle: string) => Promise<FollowRequestOutcome>;
  decline: (handle: string) => Promise<FollowRequestOutcome>;
  refresh: () => void;
}

export function useFollowRequestsList(): FollowRequestsListHook {
  const [requests, setRequests] = useState<SuggestedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [tick, setTick] = useState(0);
  // See useMutedAccountsList.ts's identical comment — a ref is required for
  // the concurrency guard because two synchronous clicks in the same tick
  // both read React state before either update commits.
  const pendingRef = useRef<Set<string>>(new Set());

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags, mirrors useMutedAccountsList.ts
    setLoading(true);
    setError(false);
    followsService
      .incomingRequests()
      .then((rows) => {
        if (!cancelled) setRequests(rows);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const resolve = useCallback(
    async (
      handle: string,
      action: (h: string) => Promise<boolean>,
      onSuccessOutcome: FollowRequestOutcome,
    ): Promise<FollowRequestOutcome> => {
      if (pendingRef.current.has(handle)) return 'busy';
      pendingRef.current.add(handle);
      setPending((s) => new Set(s).add(handle));
      try {
        const ok = await action(handle);
        if (ok) {
          setRequests((rows) => rows.filter((r) => r.handle !== handle));
          return onSuccessOutcome;
        }
        return 'error';
      } catch (err) {
        // Only a 404 (already handled elsewhere — another tab, or the
        // notification row beat this list to it) means "nothing left to act
        // on" — stale, drop the row. Any OTHER failure (network, 500, rate
        // limit) is a genuine error: the row must STAY, or a transient
        // failure silently erases a request the target never actually acted
        // on, with no way to retry it from this list.
        if (err instanceof ApiError && err.status === 404) {
          setRequests((rows) => rows.filter((r) => r.handle !== handle));
          return 'stale';
        }
        return 'error';
      } finally {
        pendingRef.current.delete(handle);
        setPending((s) => {
          const next = new Set(s);
          next.delete(handle);
          return next;
        });
      }
    },
    [],
  );

  const accept = useCallback(
    (handle: string) => resolve(handle, (h) => followsService.acceptRequest(h), 'accepted'),
    [resolve],
  );
  const decline = useCallback(
    (handle: string) => resolve(handle, (h) => followsService.declineRequest(h), 'declined'),
    [resolve],
  );

  return { requests, loading, error, pending, accept, decline, refresh };
}
